<template>
  <div class="embed">
    <div v-if="isLoadingConfig" class="spinner">
      <img :src="require('@/assets/loading.svg')" alt="Loading" class="centered" width="80px" height="80px">
    </div>
    <div v-else class="embed-code-wrap">
      <div class="embed__options">
        <div v-for="(option, key) in queryParams" :key="`share-option-${key}`" :class="['options__list', option.prefillName ? 'hidden' : '']">
          <div v-show="!option.prefillName" :class="['option field-row', isFieldFocused[key] ? 'focus' : '']">
            <div class="option__field field-checkbox-wrap">
              <input
                :id="`${key}-display`"
                v-model="selectedOptions"
                :value="key"
                :disabled="isDisabled(option.dependsOnParam)"
                type="checkbox"
                class="field field-checkbox"
                @focus="focusField(key)"
                @blur="offFocusField(key)"
                @change="validateInput(key)"
              />
              <label :for="`${key}-display`" :class="['label label-checkbox', isInputRequired(option.valueType) ? 'label--input' : '']">
                {{ option.displayName }}
                <input
                  v-if="isInputRequired(option.valueType)"
                  :id="`${key}-input`"
                  v-model="selectedOptionInputs[key]"
                  :disabled="isDisabled(option.dependsOnParam)"
                  :type="option.valueType"
                  v-bind="inputAttributes(option.minValue, option.maxValue)"
                  class="field option__input"
                  @change="validateInput(key)"
                />
              </label>
            </div>
          </div>
          <div v-if="inputErrors[key]" v-show="inputErrors[key].error" class="field-error">
            {{ inputErrors[key].message }}
          </div>
        </div>
      </div>
      <div class="embed__actions">
        <v-btn
          @click="copyEmbedCode()"
          outlined
          :color="isCopySuccessful ? 'success' : 'primary'"
          class="qrcode__btn qrcode__btn--download"
        >
          <div class="v-icon"><img :src="require(`@/assets/${isCopySuccessful ? 'Icon_Tick.svg' : 'Icon_Copy.svg'}`)" class="v-icon__icon icon icon-copy" alt=""></div>
          {{ isCopySuccessful ? 'Embed code copied' : 'Copy embed code' }}
        </v-btn>
      </div>
    </div>
  </div>
</template>

<script lang="js">
import Vue from 'vue';
import { mapMutations, mapState } from 'vuex';
import Axios from 'axios';
import { embedQueryParamDefaultLang, subscriptionTierFree } from "@/store/modules/Constants";
import { TenantHelpers } from '@/utils/tenant-helpers';

export default Vue.extend({
  name: 'EmbedCodeTab',
  components: {
  },
  props: {
    jigData: {
      type: Object,
      default: () => ({ jigId: '', jigName: '', jigDeeplink: '' }),
    },
    tierType: {
      type: String,
      default: '',
    },
  },

  /**
   * Some attributes, like `autoplay` can be set in jig itself, it might
   * be turned on by default but not reflected in iframe src url,
   * we need to make sure it's not overwritten on initial load.
   * We need to keep its default behaviour by default
   * so we won't overwrite it with `=1` or `=0`
   *
   * hasDefaultBehaviour: {
   *   autoplay: true,
   * }
   */
  data() {
    return {
      // in param-config.json default jigId field name for v-modal bind is `jig`.
      jig: this.jigData.jigId,
      name: this.jigData.jigName,
      deeplink: this.jigData.jigDeeplink,
      defaultUrl: '',
      defaultIframeEmbedHtml: '',
      backlinkHtml: '',
      queryParams: {},
      isFieldFocused: {},
      optionDependence: {},
      selectedOptions: [],
      selectedOptionInputs: {},
      successTimeoutID: null,
      inputErrors: {},
      hasDefaultBehaviour: {
        autoplay: true,
      },
      subscriptionTierType: this.tierType,
      freeTierPreset: ['autoplay', 'stepdelay'],
      isLoadingConfig: true,
      isCopySuccessful: false,
    };
  },
  async mounted() {
    await this.initDataLoad(true);
  },
  computed: {
    ...mapState('app', ['embedParamsConfig', 'myTenant']),
    embedQueries() {
      return this.selectedOptions.map((option) => `${option}=${this.selectedOptionInputs[option]}`).join('&')
    },

    /**
     * Some Query parameters, like `autoplay`, are native settings of Jig.
     * They can be set in Jig itself (i.e. `autoplay` sits in JigMetadata).
     * This means though there's no `autoplay` query parameter, a Jig can still be autoplaying.
     * As a result we need to make sure no `autoplay` is attached
     * as a query parameter on initial load to make sure
     * Jig can behave its default behaviour.
     * Once this option has any change (check/unckeck), we start
     * to append the query parameter and overwrite the value.
     */
    embedSrcUrl() {
      const turnedOffParams = Object.keys(this.queryParams).filter(
        (ov) =>
          !this.selectedOptions.includes(ov) &&
          !Object.values(this.optionDependence).find((dep) => dep.includes(ov)) &&
          !Object.prototype.hasOwnProperty.call(this.hasDefaultBehaviour, ov)
      )
      const offParamString = turnedOffParams.map((param) => `&${param}=${this.queryParams[param].minValue}`).join('');

      return `${this.defaultUrl}?${this.embedQueries}${offParamString}`;
    },
    isFreeTier() {
      return this.subscriptionTierType === subscriptionTierFree;
    },
    teamThemeColor() {
      return !this.isFreeTier && this.myTenant.ColorHex1 ? `#${this.myTenant.ColorHex1}` : `#${TenantHelpers.TenantDefaultColor1}`
    },
  },
  watch: {
    async jigData(val, oldVal) {
      this.jig = val.jigId;
      this.name = val.jigName;
      this.deeplink = val.jigDeeplink;

      if (val && (!oldVal || val.jigId !== oldVal.jigId)) {
        this.isCopySuccessful = false;
        this.selectedOptions = [];
        await this.initDataLoad();
      }
    },
    tierType(val) {
      this.subscriptionTierType = val;
    },
  },
  methods: {
    ...mapMutations('app', ['updateEmbedParamsConfig']),
    async initDataLoad(isInitialLoading = false) {
      await this.loadingConfig();
      this.prepareData();
      this.craftIframeEmbed({
        initialLoading: isInitialLoading,
      });
    },
    async loadingConfig() {
      if (!this.embedParamsConfig || !Object.keys(this.embedParamsConfig).length) {
        try {
          const result = await Axios.get(process.env.VUE_APP_EMBED_CONFIG_URL);

          if (result.status === 200) {
            const paramsConfig = result.data;

            this.updateEmbedParamsConfig(paramsConfig);
          } else {
            throw new Error(result);
          }
        } catch (error) {
          consoleLog(`Error happened on requesting for query param config: ${error}`);
        }
      }

      const cleanHtmlString = this.cleanDefaultIframeEmbedHtml({
        defaultIframeHtml: this.embedParamsConfig.defaultIframeEmbedHtml,
        backlinkHtml: this.embedParamsConfig.backlinkHtml,
      });
      this.defaultIframeEmbedHtml = cleanHtmlString.cleanIframeString;
      this.backlinkHtml = cleanHtmlString.cleanBacklinkString;

      this.defaultUrl = this.cleanDefaultURL(this.embedParamsConfig.defaultUrl);

      this.embedParamsConfig.queryParamDocs[embedQueryParamDefaultLang].forEach((param) => {
        this.queryParams = {
          ...this.queryParams,
          [param.name]: param,
        }
      });

      this.isLoadingConfig = false;
      this.$emit('configLoaded', {
        loading: this.isLoadingConfig,
        defaultIframeEmbedHtml: this.defaultIframeEmbedHtml,
        backlinkHtml: this.backlinkHtml,
      });
    },
    cleanDefaultIframeEmbedHtml(data) {
      const cleanIframeString = data.defaultIframeHtml
        .replace(/\\/g, '')
        .replace(/src="(.*?)"/g, 'src=""')
        .replace(/^<iframe(\s)/g, '<iframe\n\t')
        .replace(/(src="")\s/g, '$1\n\t')
        .replace(/></g, '\n><');

      const cleanBacklinkString = data.backlinkHtml
        .replace(/\\/g, '')
        .replace(/\[jigDeeplink\]/g, this.deeplink)
        .replace(/\[jigName\]/g, this.name)
        .replace(/\[jigId\]/g, this.jig)
        .replace(/\[brandColor\]/g, this.teamThemeColor)

      return {
        cleanIframeString,
        cleanBacklinkString,
      };
    },
    cleanDefaultURL(url) {
      return url.replace(/\?.*/g, '');
    },
    isInputRequired(type) {
      return type === 'number';
    },
    resetData() {
      this.isCopySuccessful = false;
      this.optionDependence = {};
      this.selectedOptions = [];
      this.selectedOptionInputs = {};
      this.inputErrors = {};
    },
    prepareData() {
      this.resetData()
      const validOptions = []

      for (const key in this.queryParams) {
        const option = this.queryParams[key]
        let queryValue = option.defaultValue

        /**
         * Set checkbox checked value here:
         *    checked box = parameter value of 1
         *    unchecked box = parameter value of 0
         * We need to make sure when toggle field is selected,
         * its value is always set to it's maximum value (1) not default value.
         * This is because default value might be 0,
         * it defines if checkbox is checked or not.
         * If we have checked value to be 0 we will
         * end up with `option=0` when checkbox is selected.
         */
        if (option.valueType === 'toggle') {
          queryValue = option.maxValue
        }

        if (key === 'jig' && this.jig) {
          queryValue = this.jig
        }

        this.selectedOptionInputs = {
          ...this.selectedOptionInputs,
          [key]: queryValue,
        }

        if (option.dependence) {
          this.optionDependence[key] = option.dependence
        }

        if (option.dependsOnParam) {
          const pramName = option.dependsOnParam.name

          if (!this.optionDependence[pramName]) {
            this.optionDependence[pramName] = []
          }

          if (!this.optionDependence[pramName].includes(key)) {
            this.optionDependence[pramName].push(key)
          }
        }

        if ((!option.prefillName || key === 'jig') && option.defaultValue) {
          if (!option.dependsOnParam) {
            validOptions.push(key)
          }

          if (!option.prefillName && this.isInputRequired(option.valueType)) {
            this.inputErrors[key] = {
              error: false,
              message: this.craftErrorMessage(option),
              valueType: option.valueType,
              defaultValue: option.defaultValue,
              minValue: option.minValue,
              maxValue: option.maxValue,
            }
          }
        }
      }

      if (!this.selectedOptions.length) {
        this.selectedOptions = [...this.selectedOptions, ...validOptions]
      }

      if (this.isFreeTier) {
        this.freeTierPreset.forEach((preset) => {
          if (!this.selectedOptions.includes(preset)) {
            this.selectedOptions.push(preset)
          }
        })
      }
    },
    craftErrorMessage(option) {
      let extraMessage = ''

      switch (option.valueType) {
        case 'number':
          if (option.minValue && !option.maxValue) {
            // Only minValue provided
            extraMessage = ` greater than ${option.minValue - 1}`
          } else if (!option.minValue && option.maxValue) {
            // Only maxValue provided
            extraMessage = ` less than ${option.maxValue + 1}`
          } else if (option.minValue && option.maxValue) {
            extraMessage = ` between ${option.minValue} and ${option.maxValue}`
          }

          return `Please type in a number${extraMessage}`
        case 'string':
          return 'Please type in valid string'
        default:
          return 'Please type in a valid value'
      }
    },
    focusField(name) {
      this.isFieldFocused = {
        ...this.isFieldFocused,
        [name]: true,
      };
    },
    offFocusField(name) {
      this.isFieldFocused = {
        ...this.isFieldFocused,
        [name]: false,
      };
    },
    isDisabled(dependsOnParam) {
      return (
        dependsOnParam &&
        ((dependsOnParam.value && !this.selectedOptions.includes(dependsOnParam.name)) ||
          (!dependsOnParam.value && this.selectedOptions.includes(dependsOnParam.name)))
      );
    },
    inputAttributes(minValue, maxValue) {
      const attributes = {};

      if (minValue) {
        attributes.min = minValue;
      }

      if (maxValue) {
        attributes.max = maxValue;
      }

      return attributes;
    },
    validateInput(name) {
      this.isCopySuccessful = false
      let valid = true;
      let inputValue;

      // Once a paremeter has been modified, overwrite its default behaviour.
      if (this.hasDefaultBehaviour[name]) {
        delete this.hasDefaultBehaviour[name];
      }

      if (this.inputErrors[name]) {
        switch (this.inputErrors[name].valueType) {
          case 'number':
            inputValue = parseInt(this.selectedOptionInputs[name], 10);

            if (isNaN(inputValue)) {
              // When string value has been filled in, reset to default value
              valid = false;
              this.selectedOptionInputs[name] = this.inputErrors[name].defaultValue;
            } else if (inputValue < this.inputErrors[name].minValue) {
              // When it's less than its min value reset with minValue
              valid = false;
              this.selectedOptionInputs[name] = this.inputErrors[name].minValue;
            } else if (inputValue > this.inputErrors[name].maxValue) {
              // When it exceeds its max value reset with maxValue
              valid = false;
              this.selectedOptionInputs[name] = this.inputErrors[name].maxValue;
            } else {
              this.selectedOptionInputs[name] = inputValue
              valid = true;
            }
            break;
          default:
            break;
        }

        this.inputErrors[name].error = !valid;
      }

      if (valid) {
        this.craftIframeEmbed({
          name,
        });
      }
    },
    craftIframeEmbed(option) {
      if (this.optionDependence[option.name] && !this.selectedOptions.includes(option.name)) {
        this.selectedOptions = [...this.selectedOptions.filter((opt) => !this.optionDependence[option.name].includes(opt))];
      }

      this.defaultIframeEmbedHtml = this.defaultIframeEmbedHtml.split(/src=".*"/).join(`src="${this.embedSrcUrl}"`);

      this.$emit('iframeEmbedUpdated', {
        defaultIframeEmbedHtml: this.defaultIframeEmbedHtml,
        backlinkHtml: this.backlinkHtml,
        initialLoading: option.initialLoading,
      });
    },
    copyEmbedCode() {
      window.navigator.clipboard.writeText(`${this.defaultIframeEmbedHtml}\n${this.backlinkHtml}`).then(
        () => {
          this.isCopySuccessful = true
        },
        () => {
          // clipboard write failed
          this.isCopySuccessful = false
        }
      );
    },
  },
});
</script>

<style lang="scss" scoped>
.embed__options {
  display: grid;
  grid-template-columns: repeat(1, 1fr);
  grid-gap: 20px 30px;

  @media screen and (min-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
  }

  .label-checkbox {
    display: flex;
    align-items: center;
    justify-content: flex-start;
  }
}

.options__list {
  &.hidden {
    display: none;
  }
}

.option__input {
  display: inline-block;
  width: 60px;
  min-width: 0;
  margin-left: 16px;
  text-align: center;
}

.embed__actions {
  display: flex;
  justify-content: flex-end;
  margin-top: 16px;
}
</style>
